// src/pages/Tickets.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import {
  FaTicketAlt,
  FaPlusCircle,
  FaInbox,
  FaCheckCircle,
  FaSearch,
  FaSyncAlt,
  FaExclamationCircle,
  FaTimes,
  FaChevronDown,
} from "react-icons/fa";

/* ----------------------------- SOLID MODAL ----------------------------- */
const Modal = ({ open, onClose, children, titleId }) => {
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prev;
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 2147483647,
        backgroundColor: "rgba(0,0,0,0.94)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 880,
          borderRadius: 20,
          boxShadow: "0 30px 120px -40px rgba(0,0,0,.8)",
          border: "1px solid rgba(255,255,255,0.08)",
          overflow: "hidden",
          backgroundColor: "#0f1528",
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

/* ----------------------------- tiny ui bits ---------------------------- */
const statusTone = (s) => {
  switch (s) {
    case "Open": return { bg: "#8b5cf6", fg: "#eef0ff" };
    case "Pending": return { bg: "#f59e0b", fg: "#1b1000" };
    case "Review": return { bg: "#1f6fff", fg: "#eef6ff" };
    case "Closed": return { bg: "#10b981", fg: "#00150e" };
    default: return { bg: "#64748b", fg: "#0b0f1a" };
  }
};

const Badge = ({ children, tone, style }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
      background: tone?.bg || "rgba(255,255,255,.08)",
      color: tone?.fg || "#eaf0ff",
      border: "1px solid rgba(255,255,255,.14)",
      ...style,
    }}
  >
    {children}
  </span>
);

const SkeletonRow = () => (
  <tr>
    {["40%", "60%", "18%", "28%", "1%"].map((w, i) => (
      <td key={i} style={{ padding: "14px 18px" }}>
        <div
          style={{
            height: i === 2 ? 24 : 10,
            width: w,
            background: "rgba(255,255,255,.08)",
            borderRadius: 10,
            animation: "pulse 1.6s ease-in-out infinite",
          }}
        />
      </td>
    ))}
  </tr>
);

/* -------------------------------- PAGE --------------------------------- */
export default function Tickets() {
  /* theme */
  const C = {
    navy0: "#060b18",
    navy1: "#081226",
    navy2: "#0a1630",
    white: "#f5f7fb",
    sub: "#cbd6f6",
    emerald: "#10b981",
    blue: "#1f6fff",
    pink: "#ec4899",
    red: "#ef4444",
    glass: "rgba(255,255,255,0.045)",
    border: "rgba(255,255,255,0.12)",
    borderSoft: "rgba(255,255,255,0.08)",
  };
  const R = { L: 18, M: 14, S: 10 };

  // pull user from localStorage
  const user =
    JSON.parse(localStorage.getItem("user")) || {
      name: "Unknown User",
      SLIITID: "",
      role: "Member",
      image: "/user.png",
    };

  /* data & ui state */
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [netErr, setNetErr] = useState("");

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("new");

  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "" });

  const [reviewModal, setReviewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");

  /* fetch my tickets */
  const fetchTickets = () => {
    if (!user?.SLIITID) {
      setTickets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNetErr("");

    fetch("https://dev.sliitleo.org/Backend/getmytickets.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sliitid: user.SLIITID }),
    })
      .then(async (res) => {
        const text = await res.text();
        try { return { ok: res.ok, json: JSON.parse(text) }; }
        catch { throw new Error(text || "Failed to load tickets"); }
      })
      .then(({ ok, json }) => {
        if (!ok || !json?.success) throw new Error(json?.error || "Failed to load tickets");
        const mine = (json.tickets || []).filter(
          (t) => String(t.user_id) === String(user.SLIITID).replace(/\D+/g, "")
        );
        setTickets(mine);
      })
      .catch((err) => {
        setNetErr(err.message || "Network error");
        setTickets([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTickets(); /* eslint-disable-next-line */ }, [user.SLIITID]);

  /* search/filter/sort */
  const filtered = useMemo(() => {
    let out = [...tickets];
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter(
        (t) =>
          String(t.subject || "").toLowerCase().includes(q) ||
          String(t.description || "").toLowerCase().includes(q) ||
          String(t.id || "").includes(q)
      );
    }
    if (statusFilter !== "all") out = out.filter((t) => String(t.status) === statusFilter);
    out.sort((a, b) => {
      const da = new Date(a.created_at || a.createdAt || 0).getTime();
      const db = new Date(b.created_at || b.createdAt || 0).getTime();
      return sortOrder === "old" ? da - db : db - da;
    });
    return out;
  }, [tickets, query, statusFilter, sortOrder]);

  /* create ticket */
  const onCreateChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onCreateSubmit = (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) return;
    setSaving(true);
    fetch("https://dev.sliitleo.org/Backend/create_my_tickets.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: user.SLIITID,
        user_name: user.name || "Unknown User",
        subject: form.subject.trim(),
        description: form.description.trim(),
      }),
    })
      .then((r) => r.json())
      .then((resp) => {
        if (!resp?.success) throw new Error(resp?.error || "Failed to create ticket");
        setForm({ subject: "", description: "" });
        setShowCreate(false);
        fetchTickets();
      })
      .catch((err) => {
        alert(err.message || "Failed to create ticket");
      })
      .finally(() => setSaving(false));
  };

  /* open review */
  const openReview = (ticketId) => {
    setReviewModal(true);
    setReviewLoading(true);
    setReviewError("");
    setSelectedTicket(null);

    fetch("https://dev.sliitleo.org/Backend/get_ticket_review.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ticket_id: ticketId, sliitid: user.SLIITID }),
    })
      .then((r) => r.json())
      .then((resp) => {
        if (!resp?.success) throw new Error(resp?.error || "Could not load ticket");
        setSelectedTicket(resp.ticket);
      })
      .catch((err) => setReviewError(err.message || "Could not load ticket"))
      .finally(() => setReviewLoading(false));
  };

  /* ----------------------------- STYLES -------------------------------- */
  const softShadow = "0 22px 70px -36px rgba(0,0,0,.7)";
  return (
    <div className="tickets-app">
      <style>{`
        :root{
          --navy0:${C.navy0}; --navy1:${C.navy1}; --navy2:${C.navy2};
          --white:${C.white}; --sub:${C.sub}; --emerald:${C.emerald}; --blue:${C.blue}; --pink:${C.pink}; --red:${C.red};
          --glass:${C.glass}; --border:${C.border}; --borderSoft:${C.borderSoft};
        }
        .tickets-app{
          display:flex; min-height:100vh; color:var(--white); overflow-x:hidden; position:relative;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Inter, "SF Pro Text", sans-serif;
          background:
            radial-gradient(1400px 900px at 5% -10%, #0e2a6a33 0%, transparent 60%),
            radial-gradient(1400px 900px at 120% 10%, #0a3b5a33 0%, transparent 60%),
            conic-gradient(from 200deg at 80% -10%, #0d1b3a 0deg, #091226 60deg, #0a1327 180deg, #07101f 300deg),
            linear-gradient(180deg, #0b0f1a, var(--navy1) 60%, var(--navy0));
        }
        .main{ width:100%; box-sizing:border-box; padding:88px 18px 80px; }
        @media (min-width:1024px){ .main{ padding:88px 56px 90px 320px; } }

        .hero{ background:var(--glass); border:1px solid var(--border); border-radius:${R.L}px; padding:14px 16px;
               backdrop-filter: blur(12px); box-shadow:${softShadow}, inset 0 1px 0 rgba(255,255,255,.06); margin-bottom:16px; }
        .heroRow{ display:flex; gap:12px; align-items:center; flex-wrap:wrap; }
        .heroBadge{ display:grid; place-items:center; width:42px; height:42px; border-radius:${R.M}px;
                    background: linear-gradient(135deg, #0e1f47, #0b1938); border:1px solid var(--borderSoft); }
        .heroTitle{ font-size:15px; font-weight:800; letter-spacing:.2px; }
        .heroActions{ margin-left:auto; display:flex; gap:8px; }

        .btn{ display:inline-flex; gap:8px; align-items:center; border-radius:${R.M}px; border:1px solid rgba(255,255,255,.14); padding:9px 12px;
              font-weight:800; color:#fff; background: rgba(255,255,255,.06); transition: transform .06s ease, filter .2s ease; }
        .btn:hover{ filter:brightness(1.05); } .btn:active{ transform: translateY(1px); }
        .btnPrimary{ background: linear-gradient(90deg, var(--emerald), #14a87f); box-shadow: 0 10px 22px -10px #10b98177; border:1px solid #14b98a66; }
        .btnGhost{ background: rgba(255,255,255,.08); }

        .filters{ display:grid; grid-template-columns: 1fr; gap:10px; margin-bottom:14px; }
        @media (min-width:900px){ .filters{ grid-template-columns: 1fr 1fr 1fr; } }
        .inputWrap{ position:relative; } .inputIcon{ position:absolute; left:12px; top:50%; transform:translateY(-50%); opacity:.6; }
        .inputText, .select{
          width:100%; border-radius:${R.M}px; border:1px solid var(--borderSoft); background:#0f1528; color:var(--white);
          padding:10px 12px 10px 38px; outline:none; font-weight:600;
        }
        .select{ padding-left:12px; appearance:none; }
        .selectWrap{ position:relative; } .selectCaret{ position:absolute; right:10px; top:50%; transform:translateY(-50%); opacity:.7; }

        .panel{ border-radius:${R.L}px; overflow:hidden; border:1px solid var(--border); background:#0f1528; box-shadow:${softShadow}; }
        .thead{ background:#101a33; border-bottom:1px solid #2f2a60; }
        th{ padding:12px 18px; text-align:left; font-size:14px; font-weight:900; letter-spacing:.2px; }
        td{ padding:14px 18px; vertical-align:top; }
        tr:hover{ background:#111a34; }

        .mobileList{ display:none; }
        @media (max-width: 767px){
          .desktopTable{ display:none; }
          .mobileList{ display:block; }
          .mobileCard{ padding:16px; border-bottom:1px solid var(--borderSoft); }
          .mobileCard:hover{ background:#111a34; }
        }

        .emptyBox{ text-align:center; color:#cfe0ff; padding:40px 12px; }

        .divider{ height:1px; background:rgba(255,255,255,.08); margin:8px 0; }

        @keyframes pulse{ 0%{opacity:.55} 50%{opacity:1} 100%{opacity:.55} }
      `}</style>

      <Sidebar />
      <Topbar user={user} />

      <main className="main">
        {/* HERO */}
        <div className="hero">
          <div className="heroRow">
            <div className="heroBadge"><FaTicketAlt size={18} /></div>
            <div className="heroTitle">Support Dashboard <span style={{ color: C.sub, fontWeight: 600 }}>/ Raise Tickets</span></div>
            <div className="heroActions">
              <button className="btn btnGhost" onClick={fetchTickets} title="Refresh">
                <FaSyncAlt /> <span style={{ display: "inline-block" }}>Refresh</span>
              </button>
              <button className="btn btnPrimary" onClick={() => setShowCreate(true)}>
                <FaPlusCircle /> Raise Ticket
              </button>
            </div>
          </div>
        </div>

        {/* FILTERS */}
        <div className="filters">
          <div className="inputWrap">
            <span className="inputIcon"><FaSearch /></span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by subject, description, or #ID…"
              className="inputText"
            />
          </div>
          <div className="selectWrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select"
            >
              <option value="all">All status</option>
              <option value="Open">Open</option>
              <option value="Pending">Pending</option>
              <option value="Review">Review</option>
              <option value="Closed">Closed</option>
            </select>
            <span className="selectCaret"><FaChevronDown /></span>
          </div>
          <div className="selectWrap">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="select"
            >
              <option value="new">Newest first</option>
              <option value="old">Oldest first</option>
            </select>
            <span className="selectCaret"><FaChevronDown /></span>
          </div>
        </div>

        {/* ERROR BAR */}
        {netErr && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#fecaca",
              background: "rgba(239,68,68,.12)",
              border: "1px solid rgba(239,68,68,.35)",
              padding: "10px 12px",
              borderRadius: R.M,
              marginBottom: 12,
            }}
          >
            <FaExclamationCircle /> <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{netErr}</span>
          </div>
        )}

        {/* LIST PANEL */}
        <div className="panel">
          {/* Desktop table */}
          <div className="desktopTable" style={{ overflowX: "auto" }}>
            <table style={{ minWidth: "100%", fontSize: 13 }}>
              <thead className="thead">
                <tr>
                  <th>Subject</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading && (<><SkeletonRow /><SkeletonRow /><SkeletonRow /></>)}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="emptyBox">
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                        <FaInbox style={{ fontSize: 28, color: C.pink }} />
                        <span>No tickets found.</span>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && filtered.map((t, idx) => (
                  <tr
                    key={`trow-${t.id}-${idx}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => openReview(t.id)}
                  >
                    <td style={{ fontWeight: 700 }}>
                      <div>
                        {t.subject}{" "}
                        <span
                          style={{
                            marginLeft: 6,
                            padding: "2px 6px",
                            background: "rgba(255,255,255,.1)",
                            borderRadius: 6,
                            color: "#cfe0ff",
                            fontSize: 11,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                          }}
                        >
                          #{t.id}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "#a8b4d9", marginTop: 4 }}>
                        by {t.user_name || "You"}
                      </div>
                    </td>
                    <td style={{ color: "#f1f5ff" }}>
                      <div style={{ maxWidth: 520, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.description}
                      </div>
                    </td>
                    <td>
                      <Badge tone={statusTone(t.status)}>{t.status}</Badge>
                    </td>
                    <td style={{ color: "#c9d4f2" }}>
                      {new Date(t.created_at || t.createdAt).toLocaleString()}
                    </td>
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile list */}
          <div className="mobileList">
            {loading && (
              <div style={{ padding: 16, animation: "pulse 1.6s ease-in-out infinite" }}>
                <div style={{ height: 14, width: 160, background: "rgba(255,255,255,.08)", borderRadius: 8, marginBottom: 8 }} />
                <div style={{ height: 10, width: 240, background: "rgba(255,255,255,.08)", borderRadius: 8, marginBottom: 8 }} />
                <div style={{ height: 22, width: 90, background: "rgba(255,255,255,.08)", borderRadius: 999 }} />
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="emptyBox">
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
                  <FaInbox style={{ fontSize: 28, color: C.pink }} />
                  <span>No tickets found.</span>
                </div>
              </div>
            )}
            {!loading && filtered.map((t, idx) => (
              <button
                key={`tcard-${t.id}-${idx}`}
                className="mobileCard"
                onClick={() => openReview(t.id)}
                style={{ width: "100%", textAlign: "left", background: "transparent" }}
              >
                <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>
                      {t.subject}
                      <span
                        style={{
                          marginLeft: 6,
                          padding: "2px 5px",
                          background: "rgba(255,255,255,.1)",
                          borderRadius: 6,
                          color: "#cfe0ff",
                          fontSize: 10,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                        }}
                      >
                        #{t.id}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#a8b4d9", marginTop: 4 }}>
                      {String(t.description || "").slice(0, 140)}
                      {String(t.description || "").length > 140 ? "…" : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "#9fb1e8", marginTop: 4 }}>
                      {new Date(t.created_at || t.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <Badge tone={statusTone(t.status)}>{t.status}</Badge>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ------------------------ CREATE MODAL (SOLID) ------------------------ */}
        <Modal open={showCreate} onClose={() => setShowCreate(false)} titleId="raiseTicket">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <h3 id="raiseTicket" style={{ fontSize: 16, fontWeight: 900, display: "flex", alignItems: "center", gap: 10, color: C.pink }}>
              <FaTicketAlt /> Raise a New Ticket
            </h3>
            <button
              onClick={() => setShowCreate(false)}
              aria-label="Close"
              style={{ color: "rgba(255,255,255,.8)", fontSize: 22, lineHeight: 1, background: "transparent", border: "none", cursor: "pointer" }}
            >
              <FaTimes />
            </button>
          </div>

          <form onSubmit={onCreateSubmit} style={{ padding: 20 }}>
            <label style={{ display: "block", marginBottom: 6, color: "rgba(255,255,255,.86)", fontWeight: 700 }}>Subject</label>
            <input
              name="subject"
              value={form.subject}
              onChange={onCreateChange}
              placeholder="Enter subject"
              required
              autoFocus
              style={{
                width: "100%",
                marginBottom: 14,
                padding: "12px 14px",
                borderRadius: 14,
                background: "#0b1220",
                color: C.white,
                border: "1px solid rgba(255,255,255,0.10)",
                outline: "none",
                fontWeight: 600,
              }}
            />

            <label style={{ display: "block", marginBottom: 6, color: "rgba(255,255,255,.86)", fontWeight: 700 }}>Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={onCreateChange}
              rows={6}
              placeholder="Describe your problem..."
              required
              style={{
                width: "100%",
                marginBottom: 18,
                padding: "12px 14px",
                borderRadius: 14,
                background: "#0b1220",
                color: C.white,
                border: "1px solid rgba(255,255,255,0.10)",
                outline: "none",
                fontWeight: 600,
                resize: "vertical",
              }}
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="btn"
                style={{ background: "rgba(255,255,255,.10)" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn btnPrimary"
                style={{
                  opacity: saving ? 0.7 : 1,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {saving ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </Modal>

        {/* ------------------------ REVIEW MODAL (SOLID) ------------------------ */}
        <Modal open={reviewModal} onClose={() => setReviewModal(false)} titleId="ticketReview">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <h2 id="ticketReview" style={{ fontSize: 16, fontWeight: 900, color: C.pink, display: "flex", alignItems: "center", gap: 8 }}>
              <FaTicketAlt /> Ticket Review
            </h2>
            <button
              onClick={() => setReviewModal(false)}
              aria-label="Close"
              style={{ color: "rgba(255,255,255,.8)", fontSize: 22, lineHeight: 1, background: "transparent", border: "none", cursor: "pointer" }}
            >
              <FaTimes />
            </button>
          </div>

          <div style={{ padding: 20 }}>
            {reviewLoading && <div style={{ color: C.pink, fontWeight: 800 }}>Loading…</div>}
            {reviewError && <div style={{ color: C.red, fontWeight: 800 }}>{reviewError}</div>}

            {selectedTicket && !reviewLoading && !reviewError && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  borderRadius: 16,
                  padding: 18,
                  background: "#0b1220",
                  border: "1px solid rgba(255,255,255,0.10)",
                  boxShadow: softShadow,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      display: "grid",
                      placeItems: "center",
                      width: 56,
                      height: 56,
                      borderRadius: 999,
                      color: "#fff",
                      fontSize: 22,
                      background: "linear-gradient(135deg,#ec4899,#be185d)",
                    }}
                  >
                    <FaTicketAlt />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
                      {selectedTicket.subject}
                      <span
                        style={{
                          padding: "3px 8px",
                          background: "rgba(255,255,255,.1)",
                          borderRadius: 8,
                          color: "#cfe0ff",
                          fontSize: 12,
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                        }}
                      >
                        #{selectedTicket.id}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#9fb1e8", marginTop: 2 }}>{selectedTicket.user_name}</div>
                  </div>
                </div>

                <div className="divider" />

                <div>
                  <span style={{ color: "#cfe0ff", fontWeight: 700, fontSize: 12 }}>Description:</span>
                  <div style={{ color: "#f1f5ff", marginTop: 6, whiteSpace: "pre-line" }}>{selectedTicket.description}</div>
                </div>

                <div>
                  <span style={{ color: "#cfe0ff", fontWeight: 700, fontSize: 12 }}>Response:</span>
                  <div style={{ color: "#f1f5ff", marginTop: 6, whiteSpace: "pre-line" }}>{selectedTicket.review || "—"}</div>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 6 }}>
                  <Badge tone={statusTone(selectedTicket.status)}>{selectedTicket.status}</Badge>
                  <span style={{ fontSize: 11, color: "#a8b4d9" }}>Created: {selectedTicket.created_at || selectedTicket.createdAt}</span>
                  <span style={{ fontSize: 11, color: "#bbf7d0" }}>Reviewed: {selectedTicket.review_date || "—"}</span>
                  {selectedTicket.review_name && (
                    <span style={{ fontSize: 11, color: "#e5edff" }}>Review By: {selectedTicket.review_name}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      </main>
    </div>
  );
}
